import { jsx as _jsx } from "react/jsx-runtime";
import { useRef, useContext } from 'react';
import { t } from 'i18next';
import globalStore from '../store';
import '../styles/components/blocksComponent.scss';
import customEventHandler from '../helper/customEvents';
import { detectManualTileSnap, findSnapTargetManualTile } from '../helper/helper';
import { DataContext } from '../player/DataContext';
import ButtonComponent from './ButtonComponent';
var TILE_COLORS = [
    { name: 'red', color: '#f73530', tileKey: 'text.dragToAddRedTile' },
    { name: 'blue', color: '#2a90f7', tileKey: 'text.dragToAddBlueTile' },
    { name: 'yellow', color: '#f4c500', tileKey: 'text.dragToAddYellowTile' },
    { name: 'green', color: '#1dac78', tileKey: 'text.dragToAddGreenTile' },
];
var BlocksComponent = function (props) {
    var disabled = props.disabled;
    var _a = globalStore.useStoreState(function (store) { return store.playerStore; }), zoomLevel = _a.zoomLevel, playerBgPattern = _a.playerBgPattern;
    var _b = useContext(DataContext), manipulative = _b.manipulative, getPositionForNewItem = _b.getPositionForNewItem, selectedTool = _b.selectedTool, selectedManipulativeId = _b.selectedManipulativeId;
    var dragStartPos = useRef({ x: 0, y: 0 });
    var handleDragStart = function (e, color) {
        if (disabled)
            return;
        e.preventDefault();
        e.stopPropagation();
        var clientX = 'clientX' in e ? e.clientX : e.touches[0].clientX;
        var clientY = 'clientY' in e ? e.clientY : e.touches[0].clientY;
        dragStartPos.current = { x: clientX, y: clientY };
        // Defensive: a ghost/canvasGhost from a prior drag whose onUp never ran
        // (e.g. an interrupted drag, or a dev-only HMR module swap mid-drag)
        // would otherwise sit in the DOM forever — nothing in a fresh drag's
        // closure can reach and remove someone else's orphaned node.
        document.querySelectorAll('.block-ghost, .block-canvas-ghost').forEach(function (el) { return el.remove(); });
        var scaledCellSize = 110 * zoomLevel;
        var halfScaledCellSize = scaledCellSize / 2;
        var ghost = document.createElement('div');
        ghost.className = 'block-ghost';
        ghost.style.backgroundColor = color;
        ghost.style.width = "".concat(scaledCellSize, "px");
        ghost.style.height = "".concat(scaledCellSize, "px");
        ghost.style.left = "".concat(clientX - halfScaledCellSize, "px");
        ghost.style.top = "".concat(clientY - halfScaledCellSize, "px");
        document.body.appendChild(ghost);
        customEventHandler.dispatchEvent('blocksComponent', { id: 'blockDragStarted' });
        var canvasGhost = document.createElement('div');
        canvasGhost.className = 'block-canvas-ghost';
        canvasGhost.style.display = 'none';
        canvasGhost.style.width = "".concat(scaledCellSize, "px");
        canvasGhost.style.height = "".concat(scaledCellSize, "px");
        var canvasContainer = document.querySelector('.whiteboardScrollbar .customScrollbarContent');
        if (canvasContainer) {
            canvasContainer.appendChild(canvasGhost);
        }
        // .mainHolder is the canvas element that actually carries
        // `transform: scale(zoomLevel)` — its rect already folds in scroll
        // position AND the `margin: auto` centering on its ancestor, which
        // .customScrollbarContent's own rect does not. Converting screen
        // coordinates via .customScrollbarContent's rect (as this file used
        // to) only happens to work out when the canvas exactly fills the
        // scroll viewport (zoom 100% with no centering gap) — any other zoom
        // level introduces an offset equal to that centering gap, so the
        // drop lands away from the actual mouse-up point. This mirrors the
        // established pattern already used by FractionButton/BottomPanel/etc.
        var mainHolder = document.querySelector('.mainHolder');
        var scrollbarContainer = document.querySelector('.whiteboardScrollbar');
        var hasDragged = false;
        var onMove = function (moveEvent) {
            if (moveEvent.cancelable)
                moveEvent.preventDefault();
            var moveX = 'clientX' in moveEvent ? moveEvent.clientX : moveEvent.touches[0].clientX;
            var moveY = 'clientY' in moveEvent ? moveEvent.clientY : moveEvent.touches[0].clientY;
            ghost.style.left = "".concat(moveX - halfScaledCellSize, "px");
            ghost.style.top = "".concat(moveY - halfScaledCellSize, "px");
            if (dragStartPos.current.x !== 0) {
                var dx = moveX - dragStartPos.current.x;
                var dy = moveY - dragStartPos.current.y;
                if (Math.sqrt(dx * dx + dy * dy) > 5) {
                    hasDragged = true;
                }
            }
            if (mainHolder) {
                var rect = mainHolder.getBoundingClientRect();
                if (moveX >= rect.left &&
                    moveX <= rect.right &&
                    moveY >= rect.top &&
                    moveY <= rect.bottom) {
                    var cellSize = 110;
                    // .mainHolder is the element carrying `transform: scale(zoomLevel)`;
                    // its rect already accounts for scroll AND the `margin: auto`
                    // centering on its ancestor. Converting via .customScrollbarContent's
                    // rect instead only happened to work at zoom 100% (no centering gap);
                    // any other zoom introduced an offset that got amplified by the
                    // divide-by-zoomLevel below. Matches the pattern already used by
                    // FractionButton/BottomPanel/etc.
                    var px = (moveX - rect.left) / zoomLevel;
                    var py = (moveY - rect.top) / zoomLevel;
                    // First try snapping to existing manual tiles
                    var rawDropX = px - cellSize / 2;
                    var rawDropY = py - cellSize / 2;
                    var snapResult = detectManualTileSnap({ x: rawDropX, y: rawDropY }, { width: cellSize, height: cellSize }, manipulative, undefined, 40, cellSize, 'manual');
                    // detectManualTileSnap returns the input position UNCHANGED when
                    // no existing tile is close enough to snap onto — that is not the
                    // same as "snap to the background grid". Only fall back to a
                    // grid-aligned position when a visible square grid is actually on
                    // screen to align to (playerBgPattern === 'square'); otherwise
                    // showing a "snapped" ghost at an arbitrary round-to-cellSize spot
                    // in absolute canvas coordinates just floats disconnected from the
                    // cursor and from every real tile.
                    var hasCoordinateMatch = snapResult.x !== rawDropX || snapResult.y !== rawDropY;
                    // detectManualTileSnap also performs a looser "edge alignment"
                    // pass that can nudge just one axis toward a nearby tile within
                    // a generous magnet radius, even when nothing will actually
                    // merge/attach there — that's fine for POSITIONING a fresh
                    // standalone tile neatly, but showing the merge-style ghost for
                    // it is misleading (looks like it'll attach to something when it
                    // won't). Only treat it as a "real" match for the ghost/hover
                    // indicator when findSnapTargetManualTile — the same check the
                    // actual drop uses — confirms a genuine edge or inside merge.
                    var hasRealSnapMatch = hasCoordinateMatch && !!findSnapTargetManualTile({ x: snapResult.x, y: snapResult.y }, { width: cellSize, height: cellSize }, manipulative, undefined, 40, cellSize, 'manual');
                    var hasVisibleGrid = playerBgPattern === 'square';
                    var showGhost = hasVisibleGrid;
                    var snapX = hasRealSnapMatch
                        ? snapResult.x
                        : Math.round(rawDropX / cellSize) * cellSize;
                    var snapY = hasRealSnapMatch
                        ? snapResult.y
                        : Math.round(rawDropY / cellSize) * cellSize;
                    canvasGhost.style.display = showGhost ? 'block' : 'none';
                    if (showGhost) {
                        // canvasGhost is `position: absolute` but its DOM parent
                        // (.customScrollbarContent) is `position: static` — the
                        // browser resolves its positioning context up to the next
                        // positioned ancestor instead, which is the OUTER
                        // .customScrollbarContainer (`.whiteboardScrollbar`) that
                        // never scrolls or resizes with zoom. So a world-space
                        // coordinate must be converted to a SCREEN position first
                        // (via .mainHolder's rect, which folds in scroll + zoom +
                        // centering), then back to a position local to that outer,
                        // stationary container.
                        var ghostAnchor = scrollbarContainer !== null && scrollbarContainer !== void 0 ? scrollbarContainer : canvasContainer;
                        if (ghostAnchor) {
                            var containerRect = ghostAnchor.getBoundingClientRect();
                            canvasGhost.style.left = "".concat(rect.left + snapX * zoomLevel - containerRect.left, "px");
                            canvasGhost.style.top = "".concat(rect.top + snapY * zoomLevel - containerRect.top, "px");
                        }
                    }
                    if (hasRealSnapMatch) {
                        customEventHandler.dispatchEvent('blocksComponent', {
                            id: 'blockDragging',
                            data: { x: snapX, y: snapY, w: cellSize, h: cellSize },
                            sourceId: 'panelBlock',
                        });
                    }
                    else {
                        customEventHandler.dispatchEvent('blocksComponent', { id: 'blockDragEnded' });
                    }
                }
                else {
                    canvasGhost.style.display = 'none';
                }
            }
        };
        var onUp = function (upEvent) {
            var _a, _b;
            if (upEvent.cancelable)
                upEvent.preventDefault();
            var upX = 'clientX' in upEvent ? upEvent.clientX : upEvent.changedTouches[0].clientX;
            var upY = 'clientY' in upEvent ? upEvent.clientY : upEvent.changedTouches[0].clientY;
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
            window.removeEventListener('touchmove', onMove);
            window.removeEventListener('touchend', onUp);
            if (ghost.parentNode)
                ghost.parentNode.removeChild(ghost);
            if (canvasGhost.parentNode)
                canvasGhost.parentNode.removeChild(canvasGhost);
            customEventHandler.dispatchEvent('blocksComponent', { id: 'blockDragEnded' });
            if (!hasDragged) {
                // Tap / click (no drag) → selection-aware placement (fills the
                // selected outline, or places a non-merging tile around a
                // selected random/manual tile). Drag keeps the snap/merge flow
                // below.
                placeSelectionAware(color);
                return;
            }
            if (mainHolder) {
                var rect = mainHolder.getBoundingClientRect();
                if (upX >= rect.left &&
                    upX <= rect.right &&
                    upY >= rect.top &&
                    upY <= rect.bottom) {
                    var cellSize = 110;
                    // See the matching comment in onMove above: .mainHolder's rect
                    // (not .customScrollbarContent's) is the correct zoom+scroll+
                    // centering-aware reference for converting screen → canvas
                    // coordinates.
                    var px = (upX - rect.left) / zoomLevel;
                    var py = (upY - rect.top) / zoomLevel;
                    var rawDropX = px - cellSize / 2;
                    var rawDropY = py - cellSize / 2;
                    var finalX = rawDropX;
                    var finalY = rawDropY;
                    // Match the drag-preview threshold (40) so the drop lands where the
                    // user saw the snap ghost.
                    var snapResult = detectManualTileSnap({ x: rawDropX, y: rawDropY }, { width: cellSize, height: cellSize }, manipulative, undefined, 40, cellSize, 'manual');
                    finalX = snapResult.x !== rawDropX || snapResult.y !== rawDropY
                        ? snapResult.x
                        : Math.round(rawDropX / cellSize) * cellSize;
                    finalY = snapResult.x !== rawDropX || snapResult.y !== rawDropY
                        ? snapResult.y
                        : Math.round(rawDropY / cellSize) * cellSize;
                    // Apply snap collision check on final calculated position
                    var finalSnapTarget = findSnapTargetManualTile({ x: finalX, y: finalY }, { width: cellSize, height: cellSize }, manipulative, undefined, 40, cellSize, 'manual');
                    if (finalSnapTarget) {
                        var _c = finalSnapTarget, targetItem = _c.item, mergeDir = _c.dir, targetRow = _c.row, targetCol = _c.col;
                        console.log('[tileDrop] drag finalSnapTarget', {
                            targetId: (_a = targetItem === null || targetItem === void 0 ? void 0 : targetItem.data) === null || _a === void 0 ? void 0 : _a.id,
                            mergeDir: mergeDir,
                            targetRow: targetRow,
                            targetCol: targetCol,
                        });
                        if (mergeDir === 'INSIDE') {
                            var targetData = targetItem.data;
                            var existingColor = (_b = targetData.partColors) === null || _b === void 0 ? void 0 : _b["".concat(targetRow, "-").concat(targetCol)];
                            var isOccupied = (targetData.generationMode === 'random' && existingColor !== 'transparent') ||
                                ((targetData.generationMode === 'manual' || targetData.generationMode === 'outline') && !!existingColor);
                            if (isOccupied) {
                                console.log('[tileDrop] drag ABORTED: INSIDE cell occupied', {
                                    targetRow: targetRow,
                                    targetCol: targetCol,
                                    existingColor: existingColor,
                                });
                                return;
                            }
                        }
                    }
                    else {
                        console.log('[tileDrop] drag no snap target → fresh tile', { finalX: finalX, finalY: finalY });
                    }
                    console.log('[tileDrop] drag dispatch createColoredTile', { finalX: finalX, finalY: finalY });
                    customEventHandler.dispatchEvent('bottomPanel', {
                        id: 'createColoredTile',
                        data: {
                            color: color,
                            x: finalX,
                            y: finalY,
                        },
                    });
                }
                else {
                    console.log('[tileDrop] drag drop OUTSIDE canvas container → no drop', {
                        upX: upX,
                        upY: upY,
                    });
                }
            }
            else {
                console.log('[tileDrop] drag onUp: no mainHolder');
            }
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        window.addEventListener('touchmove', onMove, { passive: false });
        window.addEventListener('touchend', onUp);
    };
    // Keyboard activation: ButtonComponent synthesises a click on Enter / Space
    // with detail=0 (mouse clicks have detail>0). For mouse clicks we let
    // handleDragStart's drag flow take over (a click without movement is a
    // no-op). For keyboard activations we create a tile at a position that's
    // guaranteed to fall outside any existing tile's snap-merge halo, so
    // sequential Enter presses each produce a NEW tile instead of being
    // silently absorbed into the previous one.
    //
    // getPositionForNewItem returns prev + 40px each call, but createColoredTile
    // snaps anything within 40px of an existing tile's edge into a merge — so
    // two presses in a row would collide (snap into tile #1, hit the occupied
    // cell check, abort). Search outward from the anchor in a column-major
    // grid stepped by CELL + threshold so each press lands clearly free.
    var CELL = 110;
    var SNAP_THRESHOLD = 40;
    var FREE_STEP = CELL + SNAP_THRESHOLD + 10; // 160 — guarantees no merge
    var findFreeTilePosition = function (anchor) {
        // Try the anchor itself, then expand outward in a square spiral. Limit
        // the search so we don't loop forever if the visible area is packed.
        var candidates = [{ x: anchor.x, y: anchor.y }];
        for (var ring = 1; ring <= 6; ring++) {
            for (var dx = -ring; dx <= ring; dx++) {
                for (var dy = -ring; dy <= ring; dy++) {
                    if (Math.abs(dx) !== ring && Math.abs(dy) !== ring)
                        continue;
                    candidates.push({
                        x: anchor.x + dx * FREE_STEP,
                        y: anchor.y + dy * FREE_STEP,
                    });
                }
            }
        }
        for (var _i = 0, candidates_1 = candidates; _i < candidates_1.length; _i++) {
            var c = candidates_1[_i];
            var target = findSnapTargetManualTile({ x: c.x - CELL / 2, y: c.y - CELL / 2 }, { width: CELL, height: CELL }, manipulative, undefined, SNAP_THRESHOLD, CELL, 'manual');
            if (!target)
                return c;
        }
        // Fallback: return the original anchor — let createColoredTile decide.
        return anchor;
    };
    // Tidy placement for the "around the selected tile" case (random/manual).
    // Scans a row-major grid starting just to the RIGHT of the selected tile
    // and returns the first slot that won't merge with anything. Returns a
    // TOP-LEFT position (consistent with how createColoredTile reads x/y).
    // Stepping by FREE_STEP keeps every placed tile clear of merges, and the
    // row-major order avoids the diagonal sprawl the square-spiral produced.
    var findAroundSpot = function (sel, scaledCell) {
        var tileRight = sel.center.x + (sel.columns * scaledCell) / 2;
        var tileTop = sel.center.y - (sel.rows * scaledCell) / 2;
        var startX = tileRight + CELL; // clear gap to the right (> threshold)
        var startY = tileTop;
        var MAX_COLS = 6;
        var MAX_ROWS = 20;
        for (var r = 0; r < MAX_ROWS; r++) {
            for (var c = 0; c < MAX_COLS; c++) {
                var x = startX + c * FREE_STEP;
                var y = startY + r * FREE_STEP;
                var target = findSnapTargetManualTile({ x: x, y: y }, { width: CELL, height: CELL }, manipulative, undefined, SNAP_THRESHOLD, CELL, 'manual');
                if (!target)
                    return { x: x, y: y };
            }
        }
        return { x: startX, y: startY };
    };
    // The currently-selected ColoredTiles tile (the one whose right panel hosts
    // this blocks component), or null if none is selected.
    var getSelectedColoredTile = function () {
        // Prefer the single active tile (selectedManipulativeId — the tile whose
        // right panel hosts this component); fall back to a coloredTiles entry in
        // the multi-select set.
        var id = (selectedManipulativeId.startsWith('coloredTiles_') ? selectedManipulativeId : '') ||
            selectedTool.find(function (s) { return s.startsWith('coloredTiles_'); }) ||
            '';
        if (!id)
            return null;
        var item = manipulative.find(function (m) { return m.data.id === id; });
        return item && item.data.type === 'coloredTiles'
            ? item.data
            : null;
    };
    // First empty cell of a tile in row-major order, or null if the tile is full.
    var findNextEmptyCell = function (tile) {
        var _a;
        for (var r = 0; r < tile.rows; r++) {
            for (var c = 0; c < tile.columns; c++) {
                if (!((_a = tile.partColors) === null || _a === void 0 ? void 0 : _a["".concat(r, "-").concat(c)]))
                    return { r: r, c: c };
            }
        }
        return null;
    };
    // Selection-aware placement for a click / tap / keyboard activation.
    //  - Outline tile selected with an empty cell → fill the next empty cell
    //    (row-major) by dropping INSIDE it.
    //  - Random / manual tile selected (or a full outline) → place a fresh,
    //    non-merging tile at the nearest free spot just outside the tile.
    //  - Nothing selected → fall back to the viewport-based free placement.
    var placeSelectionAware = function (color) {
        var _a, _b, _c;
        if (disabled) {
            console.log('[tileDrop] placeSelectionAware aborted: disabled');
            return;
        }
        var sel = getSelectedColoredTile();
        console.log('[tileDrop] placeSelectionAware', {
            color: color,
            selectedId: (_a = sel === null || sel === void 0 ? void 0 : sel.id) !== null && _a !== void 0 ? _a : null,
            mode: (_b = sel === null || sel === void 0 ? void 0 : sel.generationMode) !== null && _b !== void 0 ? _b : null,
            rows: sel === null || sel === void 0 ? void 0 : sel.rows,
            columns: sel === null || sel === void 0 ? void 0 : sel.columns,
            scale: sel === null || sel === void 0 ? void 0 : sel.scale,
        });
        if (sel) {
            var scale = (_c = sel.scale) !== null && _c !== void 0 ? _c : 1;
            var scaledCell = CELL * scale;
            if (sel.generationMode === 'outline') {
                var empty = findNextEmptyCell(sel);
                console.log('[tileDrop] outline branch, nextEmptyCell =', empty);
                if (empty) {
                    // Fill the EXACT selected tile + cell. We pass targetTileId/row/col
                    // so the controller fills this tile directly instead of
                    // re-detecting a snap target by position (which could resolve to
                    // a different tile). x/y are kept only as a positional hint.
                    var left = sel.center.x - (sel.columns * scaledCell) / 2;
                    var top_1 = sel.center.y - (sel.rows * scaledCell) / 2;
                    var cx = left + (empty.c + 0.5) * scaledCell;
                    var cy = top_1 + (empty.r + 0.5) * scaledCell;
                    console.log('[tileDrop] outline fill dispatch (explicit target)', {
                        targetTileId: sel.id,
                        cell: empty,
                    });
                    customEventHandler.dispatchEvent('bottomPanel', {
                        id: 'createColoredTile',
                        data: {
                            color: color,
                            x: cx - CELL / 2,
                            y: cy - CELL / 2,
                            targetTileId: sel.id,
                            targetRow: empty.r,
                            targetCol: empty.c,
                        },
                    });
                    return;
                }
                // Outline full → fall through to "around" placement below.
            }
            // Random / manual / full-outline → fill a tidy row-major grid just
            // to the RIGHT of the selected tile (no diagonal sprawl). We pass
            // keepSelection so the controller does NOT switch selection to the
            // new tile — otherwise the next click would anchor on the new tile
            // and the placement would march diagonally across the canvas.
            var _d = findAroundSpot(sel, scaledCell), x_1 = _d.x, y_1 = _d.y;
            console.log('[tileDrop] around-tile dispatch', { x: x_1, y: y_1 });
            customEventHandler.dispatchEvent('bottomPanel', {
                id: 'createColoredTile',
                data: { color: color, x: x_1, y: y_1, keepSelection: true },
            });
            return;
        }
        // No tile selected → viewport-based free placement (original behavior).
        var anchor = getPositionForNewItem();
        var _e = findFreeTilePosition(anchor), x = _e.x, y = _e.y;
        console.log('[tileDrop] no-selection free dispatch', { anchor: anchor, x: x, y: y });
        customEventHandler.dispatchEvent('bottomPanel', {
            id: 'createColoredTile',
            data: { color: color, x: x, y: y },
        });
    };
    // Keyboard activation (Enter / Space synthesises a click with detail === 0).
    // Mouse / touch taps are handled by handleDragStart's onUp (no-drag path).
    var handleKeyboardActivate = function (color, e) {
        if (e.detail !== 0)
            return;
        if (disabled)
            return;
        placeSelectionAware(color);
    };
    return (_jsx("div", { className: "blocksComponent", children: TILE_COLORS.map(function (item) { return (_jsx(ButtonComponent, { defaultCSS: "blockItem ".concat(item.name).concat(disabled ? ' disabled' : ''), avoidDefaultStyle: true, avoidDisableClass: true, disabled: disabled, ariaLabelTranslated: disabled ? t('text.blocksDisabled') : t(item.tileKey), onMouseDown: function (e) { return handleDragStart(e, item.color); }, onTouchStart: function (e) { return handleDragStart(e, item.color); }, clickHandler: function (e) {
                return handleKeyboardActivate(item.color, e);
            } }, item.name)); }) }));
};
export default BlocksComponent;
